/**
 * Custom Error Classes for APS/ACC API Client
 * Provides structured error handling for better debugging and error recovery
 *
 */

export interface ErrorDetails {
  [key: string]: unknown;
  originalError?: Error | unknown;
  field?: string | null;
  resourceType?: string | null;
  resourceId?: string | null;
  retryAfter?: string | number | null;
  timeout?: number | null;
  errorCode?: string | null;
}

/**
 * Base error class for all APS/ACC API errors
 */
export class APSError extends Error {
  public readonly code: string;
  public readonly statusCode: number;
  public readonly details: ErrorDetails;
  public readonly timestamp: string;

  constructor(
    message: string,
    code: string = "APS_ERROR",
    statusCode: number = 500,
    details: ErrorDetails = {}
  ) {
    super(message);
    this.name = "APSError";
    this.code = code;
    this.statusCode = statusCode;
    this.details = details;
    this.timestamp = new Date().toISOString();

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }

  public toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      statusCode: this.statusCode,
      details: this.details,
      timestamp: this.timestamp,
      stack: this.stack,
    };
  }
}

export class AuthenticationError extends APSError {
  constructor(message: string, details: ErrorDetails = {}) {
    super(message, "AUTH_ERROR", 401, details);
    this.name = "AuthenticationError";
  }
}

export class ValidationError extends APSError {
  public readonly field: string | null;

  constructor(
    message: string,
    field: string | null = null,
    details: ErrorDetails = {}
  ) {
    super(message, "VALIDATION_ERROR", 400, { field, ...details });
    this.name = "ValidationError";
    this.field = field;
  }
}

export class NetworkError extends APSError {
  constructor(
    message: string,
    statusCode: number = 0,
    details: ErrorDetails = {}
  ) {
    super(message, "NETWORK_ERROR", statusCode, details);
    this.name = "NetworkError";
  }
}

export class NotFoundError extends APSError {
  constructor(
    message: string,
    resourceType: string | null = null,
    resourceId: string | null = null,
    details: ErrorDetails = {}
  ) {
    super(message, "NOT_FOUND_ERROR", 404, {
      resourceType,
      resourceId,
      ...details,
    });
    this.name = "NotFoundError";
  }
}
